/**
 * O texto da mensagem como o WhatsApp o mostra: negrito, itálico, riscado e
 * monoespaçado (a árvore de whatsapp-format.ts, no @erp/shared) e, na fala do
 * bot, as opções que ele mandou como botões ou lista — só para ler, ninguém
 * clica por aqui.
 *
 * Sem HTML injetado: cada nó vira um elemento React e o texto entra como
 * filho (o React escapa). Nada de dangerouslySetInnerHTML — a guarda de fonte
 * da API (whatsapp-format.test) reprova este arquivo se ele aparecer.
 */
import type { ReactNode } from 'react';
import { parseWhatsappFormatting, splitBotOptions, type WaNode } from '@erp/shared';
import { cn } from '@/lib/utils';
import { LinkifiedText } from './chat-media';

function renderNodes(nodes: WaNode[], keyPrefix: string): ReactNode[] {
  return nodes.map((node, i) => {
    const key = `${keyPrefix}${i}`;
    switch (node.kind) {
      case 'text':
        return <LinkifiedText key={key} text={node.text} />;
      case 'mono':
        return (
          <code key={key} className="rounded-sm bg-black/5 px-1 font-mono text-[0.92em]">
            {node.text}
          </code>
        );
      case 'bold':
        return <strong key={key}>{renderNodes(node.children, `${key}.`)}</strong>;
      case 'italic':
        return <em key={key}>{renderNodes(node.children, `${key}.`)}</em>;
      case 'strike':
        return <s key={key}>{renderNodes(node.children, `${key}.`)}</s>;
    }
  });
}

/** texto com a formatação do WhatsApp e os links http(s) */
export function WhatsappText({ text }: { text: string }) {
  return <>{renderNodes(parseWhatsappFormatting(text), '')}</>;
}

/**
 * A fala do bot: o corpo formatado e, embaixo, as opções que ele ofereceu.
 * Até três, lado a lado (botões); mais que isso, uma por linha (lista). Sem
 * opções reconhecidas, o texto inteiro, como veio.
 */
export function BotMessageBody({ text, className }: { text: string; className?: string }) {
  const { body, options } = splitBotOptions(text);
  return (
    <div className={className}>
      {body !== '' ? (
        <div className="text-sm break-words whitespace-pre-wrap">
          <WhatsappText text={body} />
        </div>
      ) : null}
      {options.length > 0 ? (
        <ul
          aria-label="Opções enviadas pelo bot"
          className={cn('mt-1.5 gap-1', options.length > 3 ? 'flex flex-col' : 'flex flex-wrap')}
        >
          {options.map((option, i) => (
            <li
              key={`${i}-${option}`}
              title="Opção enviada pelo bot (só leitura)"
              className={cn(
                'rounded-md border border-violet-200 bg-white px-2 py-0.5 text-center text-xs font-medium text-violet-700',
                options.length > 3 && 'text-left'
              )}
            >
              {option}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
